import { useState, useEffect, useContext, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    SttFormText,
    SttButtonDelete,
    SttButtonSave,
    SttFacilityList,
} from '../../../sporttia/all';
import { AppContext } from '../../../sporttia/AppContext';
import { useCrud } from '../../../lib/hooks';
import { PERMISSIONS } from '../../../lib/permissions';

type Facility = {
    id: number;
    name: string;
    deleted?: boolean;
};

type FacilitySetData = {
    id?: number;
    name: string | null;
    position: number | null;
    status: string;
    facilities: {
        count: number;
        rows: Facility[];
    };
};

type FacilitySetResponse = {
    facilityset: FacilitySetData;
};

type Props = {
    facilitySetId: string | number;
    onClose: () => void;
    title?: string;
};

export default function FacilitySet({
    facilitySetId,
    onClose,
    title,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [facilitySet, setFacilitySet, setProperty, Get, Post, Put, Delete] =
        useCrud<FacilitySetData>();
    const [loading, setLoading] = useState(false);

    const isCreating = facilitySetId === 'create';

    useEffect(() => {
        if (facilitySetId === 'create') {
            setFacilitySet({
                name: null,
                position: null,
                status: 'ACTIVE',
                facilities: {
                    count: 0,
                    rows: [],
                },
            });
        } else {
            Get(`/facilitysets/${facilitySetId}`, { trash: true }).then(
                (response: unknown) => {
                    const res = response as FacilitySetResponse;
                    if (res.facilityset) {
                        setFacilitySet(res.facilityset);
                    }
                },
            );
        }
    }, [facilitySetId]);

    const addFacility = useCallback(
        (facility: Facility) => {
            if (!facilitySet?.id) return;
            Post(`/facilitysets/${facilitySet.id}/facilities`, {
                idFacility: facility.id,
            }).then(() => {
                setProperty({
                    name: 'facilities',
                    value: {
                        count: facilitySet.facilities.count + 1,
                        rows: [...facilitySet.facilities.rows, facility],
                    },
                });
            });
        },
        [Post, facilitySet, setProperty],
    );

    const removeFacility = useCallback(
        (facility: Facility) => {
            if (!facilitySet?.id) return;
            Delete(
                `/facilitysets/${facilitySet.id}/facilities/${facility.id}`,
            ).then(() => {
                setProperty({
                    name: 'facilities',
                    value: {
                        count: facilitySet.facilities.count - 1,
                        rows: facilitySet.facilities.rows.filter(
                            (fct) => fct.id !== facility.id,
                        ),
                    },
                });
            });
        },
        [Delete, facilitySet, setProperty],
    );

    const save = useCallback(() => {
        if (!facilitySet) return;

        // Validate required fields
        if (!facilitySet.name?.trim()) {
            cxt?.showMessage('W', t('validation.nameRequired'));
            return;
        }

        if (
            facilitySet.position === null ||
            facilitySet.position === undefined
        ) {
            cxt?.showMessage('W', t('validation.positionRequired'));
            return;
        }

        setLoading(true);
        const { name, position, status } = facilitySet;

        if (facilitySet.id) {
            Put(`/facilitysets/${facilitySet.id}`, {
                name,
                position,
                status,
            })
                .then(() => {
                    cxt?.showMessage('S', t('Saved'));
                })
                .finally(() => setLoading(false));
        } else {
            Post(`/scs/${cxt?.sc?.id}/facilitysets`, {
                name,
                position,
                status,
            })
                .then((response: unknown) => {
                    const res = response as FacilitySetResponse;
                    if (res.facilityset) {
                        setFacilitySet(res.facilityset);
                        cxt?.showMessage('S', t('Saved'));
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [Post, Put, cxt, facilitySet, setFacilitySet, t]);

    const deleteFacilitySet = useCallback(() => {
        if (!facilitySet?.id) return;
        Delete(`/facilitysets/${facilitySet.id}`).then(() => {
            onClose();
        });
    }, [Delete, facilitySet?.id, onClose]);

    const handleStatusChange = useCallback(
        (e: SelectChangeEvent) => {
            setProperty({
                name: 'status',
                value: e.target.value,
            });
        },
        [setProperty],
    );

    if (!facilitySet) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    className: 'rounded-lg min-h-[50vh]',
                },
            }}
        >
            <DialogTitle className="flex items-center justify-between pb-2">
                <Typography variant="h6" component="span" fontWeight={600}>
                    {title ||
                        (isCreating
                            ? t('CreateFacilityGroup')
                            : facilitySet.name)}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: facilitySet.id ? 6 : 12 }}>
                        <Grid container spacing={3}>
                            <SttFormText
                                grid
                                md={facilitySet.id ? 12 : 6}
                                sm={12}
                                xs={12}
                                name="name"
                                caption={t('Name')}
                                defVal={facilitySet.name}
                                onChange={setProperty}
                                required
                            />

                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: facilitySet.id ? 6 : 3,
                                }}
                            >
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                >
                                    <InputLabel>{t('Status')}</InputLabel>
                                    <Select
                                        value={facilitySet.status ?? ''}
                                        onChange={handleStatusChange}
                                        label={t('Status')}
                                    >
                                        <MenuItem value="ACTIVE">
                                            {t('FS.ACTIVE')}
                                        </MenuItem>
                                        <MenuItem value="INACTIVE">
                                            {t('FS.INACTIVE')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <SttFormText
                                grid
                                md={facilitySet.id ? 6 : 3}
                                sm={6}
                                xs={12}
                                name="position"
                                caption={t('Position')}
                                defVal={
                                    facilitySet.position !== null
                                        ? String(facilitySet.position)
                                        : null
                                }
                                onChange={setProperty}
                                required
                            />
                        </Grid>
                    </Grid>

                    {facilitySet.id && (
                        <Grid
                            size={{ xs: 12, md: 6 }}
                            className="flex [&>*]:flex-1"
                        >
                            <SttFacilityList
                                permission={PERMISSIONS.FACILITY.MODIFY}
                                facilities={
                                    facilitySet.facilities?.rows.filter(
                                        (row) => !row.deleted,
                                    ) || []
                                }
                                onAdd={addFacility}
                                onDelete={removeFacility}
                                status="ACTIVE,ONLY_SC"
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions className="p-4 gap-2 justify-start">
                {facilitySet.id && (
                    <SttButtonDelete
                        onClick={deleteFacilitySet}
                        permission={PERMISSIONS.FACILITY.MODIFY}
                    />
                )}
                <SttButtonSave
                    loading={loading}
                    onClick={save}
                    permission={PERMISSIONS.FACILITY.MODIFY}
                >
                    {t('Save')}
                </SttButtonSave>
            </DialogActions>
        </Dialog>
    );
}
